// src/modules/units/tyres/views/retread-batch/retreadbatchdispatch.js
//
// Orchestrator for dispatching a DRAFT. Confirms (with an optional dispatch date
// and delivery-note reference), then calls dispatchRetreadBatch — which moves the
// tyres to At Retread and emits the events, so the detail behind refetches.
//
// A successful dispatch may carry a soft RETREAD_CAP_REACHED warning (a tyre at or
// past its retread limit still goes): that's surfaced as an amber note with a
// "Back to batch" button rather than a block. A retryable failure (5xx, or an
// operation left FAILED) is resumed by replaying the exact request via
// resumeRetreadOperation — never a fresh dispatch, which could double-apply.

import { dispatchRetreadBatch, resumeRetreadOperation } from '../retread-batch.api.js';
import { RETREAD_WARNING, RECOVERY } from '../../shared/retread-batch-core.js';
import { confirmHTML, dispatchFieldsHTML } from './retreadbatchconfirm.view.js';
import { todayInputValue, isoFromDateInput } from '../../shared/tyre-dates.js';

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

/**
 * @param {HTMLElement} viewEl
 * @param {object} ctx
 * @param {object} ctx.batch  the batch (needs retreadBatchId; lineCount for the summary)
 * @param {string} [ctx.supplierName]
 * @param {() => void} [ctx.onClose] cancel/back
 * @param {() => void} [ctx.onDone]  a successful dispatch -> back to the (refetched) detail
 * @returns {() => void} teardown
 */
export function renderRetreadBatchDispatch(viewEl, ctx = {}) {
  let disposed = false;
  const batch = ctx.batch || {};
  const batchId = batch.retreadBatchId;
  const n = Number(batch.lineCount);
  const countBit = Number.isFinite(n) && n > 0 ? `${n} ${n === 1 ? 'tyre' : 'tyres'}` : 'the tyres';
  const toBit = ctx.supplierName ? ` to ${ctx.supplierName}` : '';

  const root = el(`<div class="tm-rb-dispatch-root">${confirmHTML({
    title: 'Dispatch batch',
    sub: `Send ${countBit}${toBit} for retread. They move to At Retread until received back.`,
    fieldsHTML: dispatchFieldsHTML({ dispatchedAt: todayInputValue() }),
    confirmLabel: 'Dispatch'
  })}</div>`);
  viewEl.replaceChildren(root);

  const msgEl = root.querySelector('[data-confirm-msg]');
  const okBtn = root.querySelector('[data-confirm-ok]');
  const cancelBtn = root.querySelector('[data-confirm-cancel]');
  const fields = Object.fromEntries(
    Array.from(root.querySelectorAll('[data-confirm-field]')).map((nd) => [nd.dataset.confirmField, nd])
  );

  let mode = 'confirm';    // 'confirm' | 'retry' | 'done'
  let retryRes = null;

  function setMsg(text, kind) {
    if (!msgEl) return;
    msgEl.textContent = text || '';
    msgEl.hidden = !text;
    msgEl.className = `tm-form-msg${kind ? ` tm-form-msg-${kind}` : ''}`;
  }
  function setBusy(on) {
    if (okBtn) okBtn.disabled = on;
    if (cancelBtn) cancelBtn.disabled = on;
    Object.values(fields).forEach((nd) => { nd.disabled = on; });
  }
  function setPrimary(label, nextMode) {
    mode = nextMode;
    if (okBtn) okBtn.textContent = label;
  }

  function handleResult(res) {
    if (disposed) return;
    setBusy(false);

    if (res.success) {
      const capped = (Array.isArray(res.warnings) ? res.warnings : [])
        .filter((w) => w?.code === RETREAD_WARNING.RETREAD_CAP_REACHED);
      if (capped.length) {
        const list = capped.map((w) => w.externalTyreId).filter(Boolean).join(', ');
        setMsg(`Dispatched. At or past the retread limit${list ? `: ${list}` : ''} — check those casings.`, 'warn');
        Object.values(fields).forEach((nd) => { nd.disabled = true; });
        if (cancelBtn) cancelBtn.hidden = true;
        setPrimary('Back to batch', 'done');
      } else if (typeof ctx.onDone === 'function') {
        ctx.onDone();
      }
      return;
    }

    retryRes = null;
    if (res.status === 403) {
      setMsg('You need administrator access to dispatch this batch.', 'error');
      setPrimary('Dispatch', 'confirm');
    } else if (res.recovery === RECOVERY.WAIT || res.locked) {
      setMsg('The batch is busy with another operation — refresh the batch and try again shortly.', 'error');
      setPrimary('Dispatch', 'confirm');
    } else if (res.recovery === RECOVERY.RETRY && res._replay) {
      retryRes = res;
      setMsg(`${res.message || 'Dispatch didn’t complete.'} You can retry safely.`, 'error');
      setPrimary('Retry dispatch', 'retry');
    } else {
      setMsg(res.message || 'Could not dispatch the batch.', 'error');
      setPrimary('Dispatch', 'confirm');
    }
  }

  async function doDispatch() {
    const payload = {};
    const iso = isoFromDateInput(fields.dispatchedAt?.value);
    if (iso) payload.dispatchedAt = iso;
    const ref = (fields.dispatchReference?.value || '').trim();
    if (ref) payload.dispatchReference = ref;
    setBusy(true);
    setMsg('Dispatching…');
    handleResult(await dispatchRetreadBatch(batchId, payload, { reason: 'dispatch' }));
  }

  async function doResume() {
    if (!retryRes) return;
    setBusy(true);
    setMsg('Retrying…');
    handleResult(await resumeRetreadOperation(retryRes, { reason: 'dispatch' }));
  }

  okBtn?.addEventListener('click', () => {
    if (mode === 'done') { if (typeof ctx.onDone === 'function') ctx.onDone(); return; }
    if (mode === 'retry') { doResume(); return; }
    doDispatch();
  });
  cancelBtn?.addEventListener('click', () => { if (typeof ctx.onClose === 'function') ctx.onClose(); });

  return () => { disposed = true; };
}
